import {
  normalizePointSnapSettings,
  type EditorDisplayConfig,
  type EditorPreferences,
  type SnapSettings,
} from '@puppycad/cad-core'
import { createDefaultSketchRelationsAndSnapSettings } from '@/config/editor-settings/EditorSettings'

/**
 * 编辑器偏好补丁。
 *
 * 设计说明：
 * 1. 只允许写入可持久化的业务字段，不暴露 `projectId` / `updatedAt` 这类存储层字段；
 * 2. 调用方可以传入响应式对象，但进入仓储前必须先归一化为普通快照；
 * 3. 该类型由仓储与宿主 UI 共用，保证持久化入口语义一致。
 */
export type EditorPreferencesPatch = Partial<Omit<EditorPreferences, 'projectId' | 'updatedAt'>>

/**
 * 复制捕捉设置，确保返回值不再携带 Vue 响应式代理。
 *
 * @param snapSettings 原始捕捉设置。
 * @returns 可安全持久化的普通快照。
 */
function cloneSnapSettings(
  snapSettings: Partial<SnapSettings> | undefined,
): SnapSettings | undefined {
  if (snapSettings === undefined) {
    return undefined
  }

  const defaults = createDefaultSketchRelationsAndSnapSettings()

  return normalizePointSnapSettings({
    isSketchSnapEnabled: snapSettings.isSketchSnapEnabled ?? defaults.isSketchSnapEnabled,
    isSnapToModelGeometryEnabled: snapSettings.isSnapToModelGeometryEnabled ?? defaults.isSnapToModelGeometryEnabled,
    isAutoGeometryRelationsEnabled: snapSettings.isAutoGeometryRelationsEnabled ?? defaults.isAutoGeometryRelationsEnabled,
    snapOptionState: {
      ...snapSettings.snapOptionState,
    },
    isGridSnapOnlyWhenGridVisibleEnabled: snapSettings.isGridSnapOnlyWhenGridVisibleEnabled ?? defaults.isGridSnapOnlyWhenGridVisibleEnabled,
    angleSnapDegree: snapSettings.angleSnapDegree ?? defaults.angleSnapDegree,
  })
}

/**
 * 复制编辑显示配置，确保仓储层只接收普通对象。
 *
 * @param editorDisplay 原始编辑显示配置。
 * @returns 可安全持久化的普通快照。
 */
function cloneEditorDisplay(
  editorDisplay: EditorDisplayConfig | undefined,
): EditorDisplayConfig | undefined {
  if (editorDisplay === undefined) {
    return undefined
  }

  return {
    shadedSketchContoursEnabled: editorDisplay.shadedSketchContoursEnabled,
  }
}

/**
 * 归一化编辑器偏好补丁。
 *
 * 设计说明：
 * 1. 这里是写入 Dexie 前的最后一道收口，所有嵌套对象都要被降解为普通快照；
 * 2. 仅保留当前支持的持久化字段，避免调用方把无关运行时状态混入仓储；
 * 3. 返回值保持 patch 语义，不会为未提供字段补默认值。
 *
 * @param patch 原始偏好补丁。
 * @returns 可安全持久化的偏好补丁快照。
 */
export function normalizeEditorPreferencesPatch(
  patch: EditorPreferencesPatch,
): EditorPreferencesPatch {
  return {
    ...(patch.snapSettings !== undefined
      ? { snapSettings: cloneSnapSettings(patch.snapSettings) }
      : {}),
    ...(patch.editorDisplay !== undefined
      ? { editorDisplay: cloneEditorDisplay(patch.editorDisplay) }
      : {}),
    ...(patch.publishedVersion !== undefined
      ? { publishedVersion: patch.publishedVersion }
      : {}),
  }
}
